import type { ReactNode } from 'react';
import { cn } from './cn';

export type StatusTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export type StatusPillProps = {
  tone?: StatusTone;
  /** Always a word. Color is never the only signal (Section 9, accessibility floor). */
  children: ReactNode;
  /** A short glyph or icon element shown before the word. */
  glyph?: ReactNode;
  className?: string;
  title?: string;
};

const tones: Record<StatusTone, string> = {
  ok: 'text-ok border-ok',
  warn: 'text-warn border-warn',
  danger: 'text-danger border-danger',
  info: 'text-info border-info',
  neutral: 'text-inkMuted border-line',
};

/** Shape plus a word plus a color. Never a bare colored dot. */
export function StatusPill({ tone = 'neutral', children, glyph, className, title }: StatusPillProps) {
  return (
    <span
      title={title}
      className={cn(
        // max-w-full and a wrapping word: at 200 percent zoom the longest
        // label ("Todo enviado") is wider than the viewport, and a pill that
        // could not wrap took the whole header sideways with it (defect D9).
        'inline-flex max-w-full items-center gap-1.5 rounded border px-2 py-1 text-caption bg-surface',
        tones[tone],
        className,
      )}
    >
      {glyph && <span aria-hidden="true">{glyph}</span>}
      <span className="min-w-0 break-words">{children}</span>
    </span>
  );
}
